/**
 * Practice seeds: a section's three simulated students (Rosa, Sam, Walter),
 * each holding a belief that some of the section's real students held,
 * built from their guide summaries. Written by
 * `apps/functions/src/guideSummaries/practiceSeeds.ts`, read server-side by
 * simteach (`app/section_teaching/practice_seeds.py`) when a section leader
 * practices with students like their own.
 */

import {z} from 'zod';

import {StudentSeedsSchema} from './studentSeed';

/** Callable name that rebuilds one section week's seeds (admins only). */
export const BUILD_PRACTICE_SEEDS_FUNCTION = 'buildpracticeseeds';

/** Request to {@link BUILD_PRACTICE_SEEDS_FUNCTION}. */
export const BuildPracticeSeedsRequestSchema = z.object({
  courseId: z.string().min(1),
  sectionId: z.string().min(1),
  sectionWeek: z.number().int().positive(),
});

/** Request to {@link BUILD_PRACTICE_SEEDS_FUNCTION}. */
export type BuildPracticeSeedsRequest = z.infer<
  typeof BuildPracticeSeedsRequestSchema
>;

/**
 * Firestore path of one section week's practice seeds.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @param params.sectionWeek The section week.
 * @returns The document path.
 * @throws Never.
 */
export function getPracticeSeedsPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: string;
  sectionId: string;
  sectionWeek: number;
}): string {
  return `${getPracticeSeedsCollectionPath({courseId, sectionId})}/${String(sectionWeek)}`;
}

/**
 * Firestore path of a section's practice seeds, one doc per section week.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @returns The collection path.
 * @throws Never.
 */
export function getPracticeSeedsCollectionPath({
  courseId,
  sectionId,
}: {
  courseId: string;
  sectionId: string;
}): string {
  return `sectionsummaries/${courseId}/sections/${sectionId}/practiceSeeds`;
}

/** One idea several students shared, as the clustering call found it. */
export const PracticeSeedClusterSchema = z.object({
  /** The idea in a student's first person, paraphrased. */
  text: z.string().trim().min(1),
  /** Short topic name, used to keep a seat's beliefs from contradicting. */
  topic: z.string().trim().min(1),
  /** Overview letters of the students who showed it. */
  students: z.array(z.string()),
  /** Of those, the letters still holding a misconception at the end. */
  stillHeldBy: z.array(z.string()).default([]),
  /**
   * For a misconception: a typical question and the wrong answer it gives,
   * in the student's voice, so a seat holding it answers concretely.
   */
  example: z.string().trim().min(1).optional(),
  /**
   * For a correct idea: indexes into the set's `misconceptions` that it is
   * the right version of. A seat never holds both.
   */
  corrects: z.array(z.number().int().nonnegative()).default([]),
});

/** One idea several students shared. */
export type PracticeSeedCluster = z.infer<typeof PracticeSeedClusterSchema>;

/** Why a seat holds what it holds, for the dashboard and audits. */
export const PracticeSeedAssignmentSchema = z.object({
  /** The seat's first name. */
  seat: z.string(),
  /** Index into `misconceptions`; omitted for a seat holding only understandings. */
  misconception: z.number().int().nonnegative().optional(),
  /** Indexes into `understandings`. */
  understandings: z.array(z.number().int().nonnegative()),
});

/** Why a seat holds what it holds. */
export type PracticeSeedAssignment = z.infer<
  typeof PracticeSeedAssignmentSchema
>;

/** One section week's practice seeds. */
export const PracticeSeedSetSchema = z.object({
  sectionWeek: z.number().int().positive(),
  /** One seed per seat that has one; simteach reads only this. */
  seeds: StudentSeedsSchema,
  /** The section's misconceptions, most prevalent first. */
  misconceptions: z.array(PracticeSeedClusterSchema),
  /** The section's shared correct ideas, most common first. */
  understandings: z.array(PracticeSeedClusterSchema),
  assignments: z.array(PracticeSeedAssignmentSchema),
  /** Students with a model-written summary this week. */
  activeStudents: z.number().int().nonnegative(),
  generatedAtMs: z.number(),
  methodKey: z.string(),
  costUsd: z.number().optional(),
});

/** One section week's practice seeds. */
export type PracticeSeedSet = z.infer<typeof PracticeSeedSetSchema>;
